/**
 * effects/gc.ts — жизненный цикл эффектов и сборка мусора (D21/R8, tech/21 §7.2).
 *
 * Снятием эффектов управляет ДВИЖОК (не карта):
 *  - авто-снятие при уходе НОСИТЕЛЯ (scope unit — юнит ушёл с поля; scope cell —
 *    ячейка освобождена) или ИСТОЧНИКА (sourceId ушёл с поля), если эффект не
 *    выставлен persistent;
 *  - duration — в РАУНДАХ; тик на границе upkeep (D9) и снятие при 0.
 *
 * Ядро прогоняет sweep после исчезновения id (смерть юнита / освобождение ячейки /
 * уход источника). Каждое снятие эмитит EffectRemoved (это делает вызывающий слой
 * через ops.removeEffect — так снятие идёт тем же путём ops → события и каскадит
 * хукам через очередь, tech/21 §5). Здесь — ЧИСТАЯ логика «кого снимать».
 *
 * Тик длительностей (tickEffectDurations) подключается к upkeep в M4 (D21):
 * M3b даёт функцию, M4 её вызывает и эмитит EffectRemoved на истёкшие.
 */
import { compareEffectsCanonical } from '../stats/effectiveStats.js';
/**
 * Юнит «на поле» = существует, zone==='board', реально занимает свою ячейку
 * (tech/22 §3). Мёртвый/ушедший юнит (cellId очищен UnitDied-reducer'ом или
 * зона сменилась) — НЕ на поле, значит его носительство/источничество прекратилось.
 */
export function isUnitOnFieldById(draft, instanceId) {
    const inst = draft.instances[instanceId];
    if (inst === undefined)
        return false;
    if (inst.zone !== 'board' || inst.cellId === undefined)
        return false;
    const cell = draft.board.cells[inst.cellId];
    return cell !== undefined && cell.occupantId === inst.id;
}
/** Исчез ли НОСИТЕЛЬ эффекта (scope unit/cell); table/lane/player не исчезают. */
function carrierGone(draft, effect) {
    const scope = effect.scope;
    switch (scope.kind) {
        case 'unit':
            return !isUnitOnFieldById(draft, scope.instanceId);
        case 'cell': {
            const cell = draft.board.cells[scope.cellId];
            return cell === undefined || cell.occupantId === null;
        }
        default:
            // table / lane / player — носитель постоянен на матч (D19); только источник.
            return false;
    }
}
/** Ушёл ли ИСТОЧНИК эффекта (sourceId с поля), если он вообще задан. */
function sourceGone(draft, effect) {
    if (effect.sourceId === undefined)
        return false;
    return !isUnitOnFieldById(draft, effect.sourceId);
}
/**
 * Нужно ли снять эффект СЕЙЧАС (D21): не persistent И (носитель исчез ИЛИ
 * источник ушёл). Длительность здесь не смотрится — её тикает upkeep (M4).
 */
export function effectShouldBeSwept(draft, effect) {
    if (effect.persistent === true)
        return false;
    if (effect.shield !== undefined)
        return carrierGone(draft, effect);
    return carrierGone(draft, effect) || sourceGone(draft, effect);
}
/**
 * ЧИСТЫЙ sweep: id эффектов, подлежащих снятию сейчас, в КАНОНИЧЕСКОМ порядке
 * эффектов (детерминизм каскада). Мутаций нет — снятие делает вызывающий слой
 * через ops.removeEffect (EffectRemoved → очередь → хуки).
 */
export function sweepEffects(draft) {
    return draft.effects
        .filter((e) => effectShouldBeSwept(draft, e))
        .sort(compareEffectsCanonical)
        .map((e) => e.id);
}
export function tickEffectDurations(draft) {
    return draft.effects
        .filter((effect) => effect.duration !== undefined)
        .sort(compareEffectsCanonical)
        .map((effect) => ({ effectId: effect.id, duration: effect.duration - 1 }));
}
//# sourceMappingURL=gc.js.map